import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Row,
  Col,
  notification,
  Modal,
  Popconfirm,
  Input,
  QRCode,
} from "antd";

import {
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  QrcodeOutlined,
  UserOutlined,
  SwapOutlined,
  LeftOutlined,
  RightOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  getUserInfo,
  handleError,
  saveTableInfo,
  getToken,
} from "../../hooks/useHttp";
import { useTableSSE } from "../../hooks/useTableSSE";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import dayjs from "dayjs";

// Frontend builds (including Cloudflare Pages) bake REACT_APP_* values at build time.
export const baseUrl = (
  process.env.REACT_APP_API_BASE_URL || "https://bip-api.bandia.vn/api"
).replace(/\/$/, "");

export const revert = (data) => {
  data.sort(() => -1);
};

const timeConvert = (timestamp) => {
  return dayjs(new Date(timestamp)).format("HH:mm:ss, DD/MM");
};

function DashBoard() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState();
  const [tableId, setTableId] = useState();
  const [amount, setAmount] = useState("");
  const currentUser = getUserInfo();
  const [modal1Open, setModal1Open] = useState(false);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [modalData, setModalData] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [logData, setLogData] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [logModalTitle, setLogModalTitle] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const { id } = useParams();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const { formatMessage } = useIntl();

  const token = getToken();
  const pageUrl = window.location.href;

  // Use SSE hook for real-time updates
  const { tableData: sseTableData } = useTableSSE(id, {
    enabled: !!id && isLoggedIn,
  });

  // Update tableData when SSE receives new data
  useEffect(() => {
    if (sseTableData && sseTableData.length > 0) {
      // Sort to keep stable order: Chicken first, then by joinedAt timestamp
      const sortedData = [...sseTableData].sort((a, b) => {
        if (a.username === "Chicken") return -1;
        if (b.username === "Chicken") return 1;
        // Sort by joinedAt to maintain stable order (API returns camelCase)
        const aTime = new Date(a.joinedAt || a.joined_at || 0).getTime();
        const bTime = new Date(b.joinedAt || b.joined_at || 0).getTime();
        return aTime - bTime;
      });
      setTableData(sortedData);
    }
  }, [sseTableData]);
 

  useEffect(() => {
    setIsLoggedIn(currentUser ? true : false);

    if (!currentUser) {
      if (id) {
        localStorage.setItem("pendingTableId", id);
      }
      navigate("/");
    }

    if (!token && currentUser) {
      notification.info({
        message: "Please log in to access this page",
        placement: "top",
      });
      navigate("/");
    }
  }, [currentUser, token, navigate, id]);

  const fetchTableData = useCallback(async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await fetch(`${baseUrl}/tables/${id}`, {
        method: "GET",
        headers,
      });

      const data = await res.json();

      if (res.ok && data) {
        setTableId(data.id);
        const players = data.players;

        const convertedData = players.map((item) => {
          return { ...item, key: item.id };
        });

        convertedData.sort((a, b) => {
          if (a.username === "Chicken") return -1;
          if (b.username === "Chicken") return 1;
          // Sort by joinedAt to maintain stable order
          const aTime = new Date(a.joinedAt || a.joined_at || 0).getTime();
          const bTime = new Date(b.joinedAt || b.joined_at || 0).getTime();
          return aTime - bTime;
        });
        setTableData(convertedData);

        ////get log data
        if (data.history) {
          const history = Object.keys(data.history).map(
            (item) => data.history[item]
          );
          setHistoryData(history);
        }
      } else {
        notification.error({
          message: data.error || "Failed to fetch table data",
          placement: "top",
        });
      }
    } catch (e) {
      handleError(e);
    }
  }, [id, token]);

  // Function to fetch all transfer history from API
  const fetchTableHistory = useCallback(async () => {
    if (!tableId) return [];

    try {
      // fetch the table data
      await fetchTableData();
      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await fetch(`${baseUrl}/tables/${tableId}/history`, {
        method: "GET",
        headers,
      });

      const data = await res.json();

      if (res.ok) {
        return data; // Return the transfers array
      } else {
        notification.error({
          message: data.error || "Failed to fetch transfer history",
          placement: "top",
        });
        return [];
      }
    } catch (e) {
      handleError(e);
      return [];
    }
  }, [tableId, token, fetchTableData]);

  useEffect(() => {
    fetchTableData();
  }, [fetchTableData, id]);

  const joinTableHandler = async (newTableId) => {
    try {
      const res = await fetch(
        `${baseUrl}/tables/${newTableId}/players/${userName}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.status === 200 || res.status === 201) {
        saveTableInfo(newTableId);
        fetchTableData();
        setIsLoggedIn(true);
      }
      if (res.status === 404) {
        navigate("/");

        const data = await res.json();
        const errMess = data.error || data.message;

        notification.error({
          message: errMess,
          placement: "top",
        });
      }
    } catch (e) {
      handleError(e);
    }
  };

  // Numpad handler
  const handleNumpadPress = (value) => {
    if (value === "delete") {
      setAmount(prev => prev.slice(0, -1));
    } else if (value === ".") {
      if (!amount.includes(".")) {
        setAmount(prev => prev + value);
      }
    } else {
      if (amount.length < 6) {
        setAmount(prev => prev + value);
      }
    }
  };

  const getPlayerLogData = async (record, type) => {
    setIsLoading(true);
    try {
      const history = await fetchTableHistory();

      if (history.length === 0) {
        setLogData([]);
        setIsLoading(false);
        return;
      }

      let filteredHistory = [];
      const playerId = record.id;

      if (type === "all") {
        // For all: get both incoming and outgoing
        filteredHistory = history.filter((item) => 
          item.fromPlayerId === playerId || item.toPlayerId === playerId
        );
      } else if (type === "out") {
        filteredHistory = history.filter((item) => item.fromPlayerId === playerId);
      } else if (type === "in") {
        filteredHistory = history.filter((item) => item.toPlayerId === playerId);
      }

      // Mark each transaction as incoming or outgoing for this player
      const processedHistory = filteredHistory.map(item => ({
        ...item,
        isIncoming: item.toPlayerId === playerId,
        displayType: item.toPlayerId === playerId ? 'in' : 'out'
      }));

      // Reverse for newest first
      setLogData(processedHistory.reverse());
    } catch (error) {
      handleError(error);
      setLogData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to get all transfer history logs
  const getAllTransferHistory = async () => {
    setIsLoading(true);
    try {
      const history = await fetchTableHistory();

      if (history.length === 0) {
        setLogData([]);
        setIsLoading(false);
        return;
      }

      // Process all history
      const processedHistory = history.map(item => ({
        ...item,
        isIncoming: false,
        displayType: 'neutral'
      }));

      // Reverse for newest first
      setLogData(processedHistory.reverse());
    } catch (error) {
      handleError(error);
      setLogData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter log data based on active tab and search
  const getFilteredLogData = () => {
    let filtered = logData;
    
    if (activeTab === "in") {
      filtered = logData.filter(item => item.displayType === 'in');
    } else if (activeTab === "out") {
      filtered = logData.filter(item => item.displayType === 'out');
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        (item.fromPlayerUsername?.toLowerCase().includes(query)) ||
        (item.toPlayerUsername?.toLowerCase().includes(query))
      );
    }

    return filtered;
  };

  // Handle transfer
  const handleTransfer = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      notification.error({
        message: "Please enter a valid amount",
        placement: "top",
      });
      return;
    }

    try {
      setIsLoading(true);
      const res = await fetch(
        `${baseUrl}/tables/${tableId}/players/${currentUser.id}/transfer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: parseFloat(amount),
            toPlayerId: modalData?.id,
          }),
        }
      );

      const data = await res.json();

      if (res.status === 400) {
        setIsLoading(false);
        const errMess = data.error || data.message;
        notification.error({
          message: errMess,
          placement: "top",
        });
      } else if (res.status === 200) {
        const message = `${formatMessage({
          id: "message.transferred",
        })} ${modalData?.username.toUpperCase()} ${amount}`;

        notification.success({
          message: message,
          placement: "top",
        });
        setModal1Open(false);
        setAmount("");
        fetchTableData();
        setIsLoading(false);
      } else {
        notification.error({
          message: data.error || "Transfer failed",
          placement: "top",
        });
        setIsLoading(false);
      }
    } catch (error) {
      handleError(error);
      setIsLoading(false);
    }
  };

  // Get balance color class
  const getBalanceClass = (points) => {
    if (points > 0) return "positive";
    return "negative"; // 0 and negative points both show as red
  };

  // Render player card
  const renderPlayerCard = (player) => {
    const balanceType = getBalanceClass(player.points);
    const isCurrentUser = currentUser.id === player.id;

    return (
      <div 
        key={player.id} 
        className={`player-card player-card-${balanceType}`}
      >
        <div className="player-name">{player.username}</div>
        <div className={`player-balance player-balance-${balanceType}`}>
          {player.points}
        </div>
        <div className="player-actions">
          {!isCurrentUser ? (
            <button 
              className="action-btn action-btn-pay"
              onClick={() => {
                setModalData(player);
                setModal1Open(true);
              }}
            >
              <DollarOutlined />
            </button>
          ) : (
            <div className="action-btn-placeholder" style={{ width: 40, height: 40 }}></div>
          )}
          <button 
            className="action-btn action-btn-log"
            onClick={() => {
              setLogModalOpen(true);
              setLogModalTitle(`Transaction History - ${player.username.toUpperCase()}`);
              setActiveTab("all");
              getPlayerLogData(player, "all");
            }}
          >
            <SwapOutlined />
          </button>
        </div>
      </div>
    );
  };

  // Render transaction card for timeline
  const renderTransactionCard = (item, index) => {
    const type = item.displayType || 'neutral';
    const isIncoming = item.isIncoming;
    const currentPlayer = isIncoming ? item.toPlayerUsername : item.fromPlayerUsername;
    const otherPlayer = isIncoming ? item.fromPlayerUsername : item.toPlayerUsername;
    
    // Get balance change for the current player's perspective
    const balanceBefore = isIncoming ? item.toBalanceBefore : item.fromBalanceBefore;
    const balanceAfter = isIncoming ? item.toBalanceAfter : item.fromBalanceAfter;
    const hasBalanceInfo = balanceBefore !== undefined && balanceBefore !== null && balanceAfter !== undefined && balanceAfter !== null;
    
    return (
      <div key={index} className="timeline-item">
        <div className={`timeline-dot timeline-dot-${type === 'in' ? 'in' : type === 'out' ? 'out' : 'neutral'}`}></div>
        <div className={`transaction-card transaction-card-${type === 'in' ? 'in' : type === 'out' ? 'out' : 'neutral'}`}>
          <div className="transaction-row">
            <div className="transaction-text">
              <strong>{currentPlayer?.toUpperCase()}</strong>
              <span className={`transaction-arrow ${isIncoming ? 'arrow-in' : 'arrow-out'}`}>
                {isIncoming ? ' ← ' : ' → '}
              </span>
              <strong>{otherPlayer?.toUpperCase()}</strong>
            </div>
            <div className={`transaction-amount transaction-amount-${type === 'in' ? 'in' : type === 'out' ? 'out' : 'neutral'}`}>
              {item.amount}
            </div>
          </div>
          <div className="transaction-footer">
            <span className="transaction-time">
              {item.timestamp ? timeConvert(item.timestamp) : ""}
            </span>
            {hasBalanceInfo && (
              <span className={`transaction-balance-change ${isIncoming ? 'balance-in' : 'balance-out'}`}>
                {balanceBefore} → {balanceAfter}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render full log transaction card (neutral style)
  const renderFullLogTransactionCard = (item, index) => {
    const hasBalanceInfo = item.fromBalanceBefore !== undefined && item.fromBalanceBefore !== null;
    
    return (
      <div key={index} className="timeline-item">
        <div className="timeline-dot timeline-dot-neutral"></div>
        <div className="transaction-card transaction-card-neutral">
          <div className="transaction-row">
            <div className="transaction-text">
              <strong>{item.fromPlayerUsername?.toUpperCase()}</strong>
              <span className="transaction-arrow arrow-out"> → </span>
              <strong>{item.toPlayerUsername?.toUpperCase()}</strong>
            </div>
            <div className="transaction-amount transaction-amount-neutral">
              {item.amount}
            </div>
          </div>
          <div className="transaction-footer">
            <span className="transaction-time">
              {item.timestamp ? timeConvert(item.timestamp) : ""}
            </span>
            {hasBalanceInfo && (
              <span className="transaction-balance-change balance-neutral">
                {item.fromBalanceBefore} → {item.fromBalanceAfter}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const filteredLogData = getFilteredLogData();

  return (
    <div className="dashboard-container">
      {!isLoggedIn && (
        <>
          <Col span={24} style={{ marginBottom: 16, marginTop: 50 }}>
            <Input
              size="large"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder={formatMessage({ id: "placeHolder.enterName" })}
            />
          </Col>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "20px",
              marginTop: 50,
            }}
          >
            <Popconfirm
              placement="topLeft"
              title="Bạn chắc chứ"
              onConfirm={() => {
                navigate("/");
              }}
              okText="OK"
              cancelText="Cancel"
            >
              <Button
                style={{ backgroundColor: "var(--primary-color)" }}
                type="primary"
              >
                {formatMessage({ id: "button.leave" })}
              </Button>
            </Popconfirm>
            <Button
              type="primary"
              onClick={() => joinTableHandler(id)}
              style={{ backgroundColor: "var(--primary-color)" }}
              disabled={userName ? false : true}
            >
              {formatMessage({ id: "button.joinTable" })}
            </Button>
            <Button
              type="primary"
              onClick={() => {
                saveTableInfo(id);
                navigate(`/over-view/${id}`);
              }}
              style={{ backgroundColor: "var(--primary-color)" }}
              disabled={userName ? false : true}
            >
              {formatMessage({ id: "button.watch" })}
            </Button>
          </div>
        </>
      )}
      {isLoggedIn && (
        <>

          {/* Player Cards */}
          <div className="player-cards-container">
            {tableData?.map(player => renderPlayerCard(player))}
          </div>

          {/* Pagination */}
          {tableData && tableData.length > 5 && (
            <div className="pagination-container">
              <button className="pagination-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>
                <LeftOutlined />
              </button>
              <span className="pagination-current">{currentPage}</span>
              <button className="pagination-btn" onClick={() => setCurrentPage(p => p + 1)}>
                <RightOutlined />
              </button>
            </div>
          )}

          {/* Bottom Actions */}
          <div className="bottom-actions">
            <Popconfirm
              placement="topLeft"
              title={formatMessage({ id: "message.confirm" })}
              onConfirm={() => {
                navigate("/");
              }}
              okText="OK"
              cancelText="Cancel"
            >
              <button className="bottom-btn bottom-btn-dark">
                {formatMessage({ id: "button.leave" })}
              </button>
            </Popconfirm>

            <button 
              className="qr-btn"
              onClick={() => setQrModalOpen(true)}
            >
              <QrcodeOutlined className="qr-btn-icon" />
              <span className="qr-btn-text">QR Code</span>
            </button>

            <button 
              className="stream-share-btn"
              onClick={() => {
                const viewerUrl = `${window.location.origin}/stream/view/${id}`;
                navigator.clipboard.writeText(viewerUrl);
                notification.success({
                  message: "Stream link copied!",
                  description: viewerUrl,
                  placement: "top",
                });
              }}
            >
              <span className="stream-share-icon">📺</span>
              <span className="stream-share-text">Stream</span>
            </button>

            <button 
              className="bottom-btn bottom-btn-dark"
              onClick={() => {
                setLogModalOpen(true);
                setLogModalTitle("Table Log");
                setActiveTab("all");
                getAllTransferHistory();
              }}
            >
              {formatMessage({ id: "button.allLog" })}
            </button>
          </div>

          {/* Footer */}
          <div className="dashboard-footer">
            From Hoangbim with <span className="heart">♥</span>
          </div>

          {/* ------------------------------------- Transfer Modal (Numpad) ---------------- */}
          <Modal
            open={modal1Open}
            onCancel={() => {
              setAmount("");
              setModal1Open(false);
            }}
            footer={null}
            centered
            closable={true}
            width={320}
            styles={{ body: { padding: 0 } }}
          >
            <div className="numpad-modal">
              <div className="numpad-title">
                Transfer to {modalData.username}
              </div>
              <div className="numpad-display">
                {amount || "0"}
              </div>
              <div className="numpad-grid">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0'].map(num => (
                  <button
                    key={num}
                    className="numpad-btn"
                    onClick={() => handleNumpadPress(num)}
                  >
                    {num}
                  </button>
                ))}
                <button
                  className="numpad-btn"
                  onClick={() => handleNumpadPress('delete')}
                >
                  <DeleteOutlined />
                </button>
              </div>
              <button
                className="numpad-confirm"
                onClick={handleTransfer}
                disabled={!amount || isLoading}
              >
                {isLoading ? "Processing..." : "Confirm Transfer"}
              </button>
            </div>
          </Modal>

          {/*---------------------- Modal Transfer Log (with Tabs) ------------------ */}
          <Modal
            title={logModalTitle}
            centered
            open={logModalOpen}
            onCancel={() => {
              setLogModalOpen(false);
              setActiveTab("all");
              setSearchQuery("");
            }}
            footer={null}
            width={400}
            className="history-modal-dark"
            styles={{ 
              content: { background: '#1a1a2e', borderRadius: 16 },
              header: { background: 'transparent', borderBottom: '1px solid rgba(75, 85, 99, 0.5)' },
              body: { background: 'transparent' }
            }}
          >
            {/* Timeline */}
            <div className="timeline-container">
              <div className="timeline-line"></div>
              {isLoading ? (
                <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20 }}>Loading...</div>
              ) : filteredLogData.length > 0 ? (
                logModalTitle === "Table Log" 
                  ? [...filteredLogData].reverse().map((item, index) => renderFullLogTransactionCard(item, index))
                  : [...filteredLogData].reverse().map((item, index) => renderTransactionCard(item, index))
              ) : (
                <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20 }}>No transactions found</div>
              )}
            </div>

            {/* Show All Log Button */}
            {logModalTitle !== "Table Log" && (
              <button 
                className="show-all-log-btn"
                onClick={() => {
                  setLogModalTitle("Table Log");
                  getAllTransferHistory();
                }}
              >
                Show all log
              </button>
            )}
          </Modal>

          {/*---------------------- Modal QR Code------------------ */}
          <Modal
            title={null}
            centered
            open={qrModalOpen}
            onCancel={() => setQrModalOpen(false)}
            footer={null}
            className="qr-display-modal"
          >
            <div className="qr-display-content">
              <h1 className="qr-display-title">My Table QR Display</h1>
              
              <div className="qr-code-frame">
                <div className="qr-code-inner">
                  <QRCode value={pageUrl} size={200} />
                </div>
              </div>
              
              <h2 className="qr-table-name">Table #{id?.slice(-6)}</h2>
              
              <div className="qr-actions">
                <button 
                  className="qr-share-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(pageUrl);
                    notification.success({
                      message: "Link copied to clipboard!",
                      placement: "top",
                    });
                  }}
                >
                  <span className="qr-btn-icon">🔗</span>
                  Share Table Link
                  <span className="qr-btn-arrow">→</span>
                </button>
                
                <button 
                  className="qr-save-btn"
                  onClick={() => {
                    const canvas = document.querySelector('.qr-code-inner canvas');
                    if (canvas) {
                      const url = canvas.toDataURL('image/png');
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `table-${id?.slice(-6)}-qr.png`;
                      a.click();
                    }
                  }}
                >
                  <span className="qr-save-icon">⬇</span>
                  <span className="qr-save-text">Save QR<br/>to Gallery</span>
                </button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}

export default DashBoard;
